// Pure helpers for the Liquid profiler flame graph. The profiler dumps a
// hierarchical tree (`{ name, value (seconds), children }`); ECharts renders a
// flame graph as a `custom` series whose data is a *flat* list of frames, each
// positioned by depth (level) and a horizontal [start, end] range in seconds.
// `buildFlameFrames` does that flattening; the behaviour feeds the result into
// a `renderItem` callback.

export interface ProfileNode {
  name: string
  value: number
  children?: ProfileNode[]
}

export interface FlameFrame {
  // Stable path-based identifier (`0`, `0.1`, `0.1.0`, …) used to zoom into a
  // subtree on click. Independent of `name`, which need not be unique.
  id: string
  name: string
  // [level, start, end, name] — the tuple ECharts' renderItem reads via
  // `api.value(i)`. `name` is duplicated here so the frame label can be drawn.
  value: [number, number, number, string]
  itemStyle: { color: string }
}

export const ROOT_FRAME_ID = '0'

// Deterministic warm colour (reds → yellows) derived from the frame name, so a
// given frame keeps a stable, flame-graph-like hue across re-renders.
function frameColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  const hue = 10 + (hash % 50) // 10–60deg
  const saturation = 65 + (hash % 20) // 65–85%
  const lightness = 50 + (hash % 15) // 50–65%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

// Walk to the node addressed by a path-based id (e.g. `0.2.1`). Returns null
// when the path no longer resolves (tree changed underneath a stale click).
function findNode(root: ProfileNode, id: string): ProfileNode | null {
  if (id === ROOT_FRAME_ID) return root
  const indices = id.split('.').slice(1).map(Number)
  let node: ProfileNode | undefined = root
  for (const index of indices) {
    node = node.children?.[index]
    if (!node) return null
  }
  return node ?? null
}

// Flatten the profile tree into frames. When `focusId` points at a subtree, that
// subtree is rendered as the new root (level 0, start 0) so the view "zooms" into
// it; child ids stay anchored to the full-tree path so a follow-up click still
// resolves.
export function buildFlameFrames(
  root: ProfileNode,
  focusId: string = ROOT_FRAME_ID
): FlameFrame[] {
  // Anchor on the resolved focus node; if a stale id no longer resolves, fall
  // all the way back to the real root so child ids stay consistent.
  const focus = findNode(root, focusId)
  const focusNode = focus ?? root
  const focusNodeId = focus ? focusId : ROOT_FRAME_ID
  const frames: FlameFrame[] = []

  const recur = (
    node: ProfileNode,
    id: string,
    level: number,
    start: number
  ): void => {
    frames.push({
      id,
      name: node.name,
      value: [level, start, start + node.value, node.name],
      itemStyle: { color: frameColor(node.name) },
    })

    let childStart = start
    ;(node.children ?? []).forEach((child, index) => {
      recur(child, `${id}.${index}`, level + 1, childStart)
      childStart += child.value
    })
  }

  recur(focusNode, focusNodeId, 0, 0)
  return frames
}

// Deepest level present in a frame list — used to size the chart container and
// cap the y axis.
export function maxFrameLevel(frames: FlameFrame[]): number {
  return frames.reduce((max, frame) => Math.max(max, frame.value[0]), 0)
}
